import { z } from 'zod';
/** Error messages are user-facing South African English and short. */
// --- Phone numbers ---------------------------------------------------------
/**
 * South African phone numbers are stored E.164 (`+27...`).
 *
 * Accepts the three shapes people actually type and normalises all of them:
 * `0821234567`, `+27 82 123 4567`, `27821234567` -> `+27821234567`.
 * The national part is 9 digits and may not start with 0.
 */
const ZA_NATIONAL_DIGITS = 9;
export function normaliseZaPhone(input) {
    const trimmed = input.trim();
    // Digits and the separators people type, with at most one leading +.
    if (!/^\+?[\d\s().-]+$/.test(trimmed))
        return null;
    const digits = trimmed.replace(/\D/g, '');
    let national;
    if (trimmed.startsWith('+')) {
        if (!digits.startsWith('27'))
            return null;
        national = digits.slice(2);
    }
    else if (digits.startsWith('27') && digits.length === ZA_NATIONAL_DIGITS + 2) {
        national = digits.slice(2);
    }
    else if (digits.startsWith('0')) {
        national = digits.slice(1);
    }
    else {
        return null;
    }
    // A national number is 9 digits and never starts with 0. SA ranges start at 1.
    if (!/^[1-8]\d{8}$/.test(national))
        return null;
    return `+27${national}`;
}
export const ZaPhone = z
    .string()
    .trim()
    .transform((value, ctx) => {
    const normalised = normaliseZaPhone(value);
    if (normalised === null) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid South African number, like 082 123 4567.' });
        return z.NEVER;
    }
    return normalised;
});
// --- Money -----------------------------------------------------------------
/** ZAR only, always integer cents. Never Float or Decimal. */
export const MoneyCents = z.int().min(0, 'Amount cannot be negative.');
/** R79,00 per month (D4). */
export const MEMBERSHIP_AMOUNT_CENTS = 7900;
// --- Human-facing identifiers ----------------------------------------------
/** 8 digits, non-sequential, shown on the membership card. */
export const MemberNumber = z
    .string()
    .trim()
    .regex(/^\d{8}$/, 'A membership number is 8 digits.');
/** Human-friendly job reference, e.g. `MH-4KQ7X2`. Unambiguous characters only. */
export const JobReference = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^MH-[2-9ACDEFGHJKLMNPQRSTUVWXYZ]{6}$/, 'Enter a job reference like MH-4KQ7X2.');
/** Estate agent referral code, e.g. `DEMI3456`. Members may type it with a leading #. */
export const ReferralCode = z
    .string()
    .trim()
    .transform((value) => value.replace(/^#/, '').toUpperCase())
    .refine((value) => /^[A-Z0-9]{4,16}$/.test(value), 'A referral code is 4 to 16 letters and numbers.');
/** The 6-digit rotating membership card code (TOTP, 60s step). */
export const CardCode = z
    .string()
    .trim()
    .regex(/^\d{6}$/, 'The code is 6 digits.');
export const Slug = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and hyphens.')
    .max(80);
// --- Common fields ---------------------------------------------------------
/** Forms send '' for an untouched optional field; treat it as absent. */
export function optional(schema) {
    return z.preprocess((value) => (value === '' ? undefined : value), schema.optional());
}
/** Normalise first, then validate, so trailing spaces and capitals are not errors. */
export const Email = z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address.').max(254));
export const PersonName = z
    .string()
    .trim()
    .min(1, 'This field is required.')
    .max(80, 'Keep this under 80 characters.');
/** 4-digit South African postal code. */
export const PostalCode = z
    .string()
    .trim()
    .regex(/^\d{4}$/, 'A postal code is 4 digits.');
export const Rating = z.int().min(1, 'Choose 1 to 5 stars.').max(5, 'Choose 1 to 5 stars.');
/** A member discount, stored whole percent. */
export const DiscountPercent = z.int().min(0).max(100);
/** Dates cross the wire as ISO 8601 UTC strings and are displayed in Africa/Johannesburg. */
export const IsoDateTime = z.iso.datetime({ offset: true });
export const Cuid2 = z.cuid2();
/** Terms acceptance records the version accepted (POPIA evidence). */
export const PolicyVersion = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A policy version is a date, like 2026-10-21.');
/** The timezone every date is displayed in. */
export const DISPLAY_TIME_ZONE = 'Africa/Johannesburg';
export const CURRENCY = 'ZAR';
export const LOCALE = 'en-ZA';
//# sourceMappingURL=primitives.js.map